import { useCallback, useEffect, useMemo, useReducer } from "react";
import { useSelector } from "react-redux";
import { AreaItemType, ConditionField, MeasureUnitTable } from "../../shared/reporting/api/biClient.types";
import { getValidConditionDescriptors } from "../../shared/reporting/utils/getValidConditions";
import cloneDeep from "../../shared/utilities/cloneDeep";
import { generateGuid } from "../../shared/utilities/generateGuid";
import {
  CellDrillDownInfoType,
  DrillDownDataRequest,
  MeasureDrillDownInfo,
  MetricDrillDownInfo,
} from "../api/biApi.types";
import { getDrillDownColumns, getSorting } from "../components/builder/common/drillDown/utils/drillDownHelper";
import { selectDimensions } from "../store/selectors/metaDataSelectors";
import { drillDownActions, drillDownReducer, initialDrillDownState } from "./drillDownState";
import { fieldSateConfigurationInitialState, fieldStateActions, fieldStateReducer } from "./fieldsState";
import { FieldWithOrder, FieldWithSorting } from "./FieldWithOrder";

export default function useDrillDown(info: MeasureDrillDownInfo | MetricDrillDownInfo) {
  const dimensions = useSelector(selectDimensions);
  const [fieldState, fieldStateDispatch] = useReducer(fieldStateReducer, fieldSateConfigurationInitialState);
  const [state, dispatch] = useReducer(drillDownReducer, initialDrillDownState);

  useEffect(() => {
    const copied = info.conditions
      .map((c) => {
        const meta = dimensions.find((d) => d.name === c.dimensionName);
        if (meta === undefined) return undefined;
        const clone = cloneDeep(c);
        const field: ConditionField = {
          meta,
          areaItemType: AreaItemType.CONDITIONS,
          config: {
            guid: generateGuid(),
            filter: clone,
          },
        };
        return field;
      })
      ?.filter((f): f is ConditionField => !!f);

    if (copied === undefined) return;

    setConditions(copied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info.conditions]);

  const drillDownConfig = useMemo(
    (): DrillDownDataRequest =>
      info.drillDownInfoType === CellDrillDownInfoType.Metric
        ? {
            columns: getDrillDownColumns(state.selectedFields),
            conditions: getValidConditionDescriptors(fieldState.conditions),
            calcTotalCount: true,
            sortingColumns: getSorting(state.sortedFields),
            skip: 0,
            take: 0,
            table: MeasureUnitTable.Gl,
            allocated: false,
          }
        : {
            columns: getDrillDownColumns(state.selectedFields),
            conditions: getValidConditionDescriptors(fieldState.conditions),
            calcTotalCount: true,
            sortingColumns: getSorting(state.sortedFields),
            skip: 0,
            take: 0,
            table: info.table || MeasureUnitTable.Gl,
            allocated: info.allocated === true,
          },
    [state.selectedFields, state.sortedFields, fieldState.conditions, info]
  );

  const setConditions = useCallback((fields: ConditionField[]) => {
    fieldStateDispatch(fieldStateActions.setConditions(fields));
  }, []);
  const addConditions = useCallback((fields: ConditionField[]) => {
    fieldStateDispatch(fieldStateActions.addConditions(fields));
  }, []);
  const addCondition = useCallback((field: ConditionField) => {
    fieldStateDispatch(fieldStateActions.addConditions([field]));
  }, []);
  const updateCondition = useCallback((field: ConditionField, changes: Partial<ConditionField>) => {
    fieldStateDispatch(fieldStateActions.updateCondition({ field, changes }));
  }, []);
  const removeCondition = useCallback((field: ConditionField) => {
    fieldStateDispatch(fieldStateActions.removeCondition(field));
  }, []);

  const setTotalRows = useCallback((num: number) => {
    dispatch(drillDownActions.setRowsTotal(num));
  }, []);

  const changeSelectedFields = useCallback((fields: FieldWithOrder[]) => {
    dispatch(drillDownActions.setSelectedFields([...fields]));
  }, []);

  const changeFieldSorting = useCallback((fields: FieldWithSorting[]) => {
    dispatch(drillDownActions.setSortedFields([...fields]));
  }, []);

  const setLoading = useCallback((value: boolean) => {
    dispatch(drillDownActions.setLoading(value));
  }, []);

  const setPagination = useCallback((skip: number, take: number) => {
    dispatch(drillDownActions.setPagination({ skip, take }));
  }, []);

  const setError = useCallback((error: string | undefined) => {
    dispatch(drillDownActions.setError(error));
  }, []);

  return {
    conditions: fieldState.conditions,
    ...state,
    drillDownConfig,
    actions: {
      setConditions,
      addCondition,
      updateCondition,
      addConditions,
      removeCondition,
      changeSelectedFields,
      changeFieldSorting,
      setTotalRows,
      setLoading,
      setPagination,
      setError,
    },
  };
}

export type DrillDownState = ReturnType<typeof useDrillDown>;
